/**
 * Parameters passed to the callback function in the invoker utility
 * @typedef {Object} InvokeUntilCallbackParams
 * @property {number} invokeCount The number of times the callback has been invoked (starts from 0)
 * @property {boolean} isInvokeLimitReached Whether the invoke limit has been reached
 */
type InvokeUntilCallbackParams = {
  invokeCount: number;
  isInvokeLimitReached: boolean;
};

type InvokeUntilOptions = {
  invokeLimit?: number;
  shouldInvokeResolver?: (params: { invokeCount: number }) => boolean;
  frequencyInMilliseconds?: number;
  immediateFirstInvoke?: boolean;
};

type InvokeUntilReturnType = {
  abortInvoker: () => void;
};

/**
 * Invokes a callback function repeatedly until certain conditions are met
 * @param callback Function to be invoked with parameters containing invoke count and limit status
 * @param options Configuration options object
 * @param options.invokeLimit Maximum number of times to invoke the callback
 * @param options.shouldInvokeResolver Optional function that determines if callback should be invoked based on current count
 * @param options.frequencyInMilliseconds Time in ms between invocations (default: 300)
 * @param options.immediateFirstInvoke Whether to invoke callback immediately before starting timer (default: false)
 * @returns Object containing abortInvoker function to cancel future invocations
 * @example
 * // Example 1: Basic usage with invoke limit
 * const { abortInvoker } = invoker(
 *   ({ invokeCount }) => console.log(`Invoked ${invokeCount} times`),
 *   { invokeLimit: 5 }
 * );
 *
 * // Example 2: Using parent scope variable to control invocation
 * let shouldContinue = true;
 * const { abortInvoker } = invoker(
 *   ({ invokeCount }) => console.log(`Still running: ${invokeCount}`),
 *   {
 *     shouldInvokeResolver: () => shouldContinue,
 *     frequencyInMilliseconds: 1000
 *   }
 * );
 * // Later: shouldContinue = false; // Will stop invocations
 */
export const invoker = (
  callback: (params: InvokeUntilCallbackParams) => void,
  {
    frequencyInMilliseconds = 300,
    immediateFirstInvoke = false,
    shouldInvokeResolver,
    invokeLimit,
  }: InvokeUntilOptions = {}
): InvokeUntilReturnType => {
  let invokeCount = 0;
  let timeoutId: ReturnType<typeof window.setTimeout> | null = null;

  const invokeLimitReached = (): boolean => invokeCount === invokeLimit;

  const executeCallback = () => {
    callback({
      invokeCount,
      isInvokeLimitReached:
        typeof invokeLimit !== "undefined" &&
        invokeLimit - invokeCount === 1 /* because of "invokeCount" starts from index 0 */,
    });
    invokeCount++;
  };

  const invokeCallback = () => {
    if (invokeLimitReached() && timeoutId !== null) {
      clearTimeout(timeoutId);
      return;
    }

    const shouldInvokeResult = shouldInvokeResolver ? shouldInvokeResolver({ invokeCount }) : true;

    if (shouldInvokeResult) {
      executeCallback();
      timeoutId = setTimeout(invokeCallback, frequencyInMilliseconds);
    }
  };

  if (!invokeLimitReached()) {
    if (immediateFirstInvoke) {
      executeCallback();
    }
    timeoutId = setTimeout(invokeCallback, frequencyInMilliseconds);
  }

  return {
    abortInvoker: () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    },
  };
};
